import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Currency } from "@/lib/currencies/currenciesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

type CurrencySelectProps = {
  value: string;
  currencies: Currency[];
  onChange: (code: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  size?: "default" | "sm";
  showSymbol?: boolean;
};

export function CurrencySelect({
  value,
  currencies,
  onChange,
  placeholder = "Select currency",
  triggerClassName,
  size = "default",
  showSymbol = true,
}: CurrencySelectProps) {
  const [open, setOpen] = useState(false);

  const selectedCurrency = currencies.find(
    (c) => c.code.toLowerCase() === (value ?? "").toLowerCase()
  );

  const displayLabel = selectedCurrency
    ? showSymbol
      ? `${selectedCurrency.code} (${selectedCurrency.symbol})`
      : selectedCurrency.code
    : value || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "glass-input w-full justify-between font-normal px-3",
            size === "sm" ? "h-9 text-xs" : "h-10 text-sm",
            !selectedCurrency && "text-muted-foreground",
            triggerClassName
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0 z-[100]" align="start" sideOffset={4}>
        <Command shouldFilter>
          <CommandInput placeholder="Search currency..." className="h-9 text-xs" />
          <CommandList className="max-h-[220px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No currency found.
            </CommandEmpty>
            <CommandGroup>
              {currencies.map((c) => {
                const isSelected = c.code.toLowerCase() === (value ?? "").toLowerCase();
                return (
                  <CommandItem
                    key={c.code}
                    value={`${c.code} ${c.name} ${c.symbol}`}
                    onSelect={() => {
                      onChange(c.code);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-1.5 truncate min-w-0">
                      <span className="font-medium">{c.code}</span>
                      <span className="opacity-80">({c.symbol})</span>
                      <span className="opacity-75 truncate">— {c.name}</span>
                    </div>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
